// hooks/useQrScanner.ts
import { useEffect, useRef } from "react";
import { Html5Qrcode, Html5QrcodeCameraScanConfig, Html5QrcodeSupportedFormats } from "html5-qrcode";

interface UseQrScannerOptions {
  elementId: string;
  onSuccess: (text: string) => void;
  onIdleTimeout?: () => void;
  beepOnSuccess?: boolean;
  idleTimeoutMs?: number;
  scanBoxSize?: number; // optional fixed override; omit for adaptive sizing
  fps?: number;
}

type CameraDevice = {
  id: string;
  label: string;
};

export const useQrScanner = ({
  elementId,
  onSuccess,
  onIdleTimeout,
  beepOnSuccess = true,
  idleTimeoutMs = 60000,
  scanBoxSize,
  fps = 12,
}: UseQrScannerOptions) => {
  const qrCodeRef = useRef<Html5Qrcode | null>(null);
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRunningRef = useRef<boolean>(false);
  const lastScanRef = useRef<{ text: string | null; time: number }>({ text: null, time: 0 });

  // const playBeep = () => {
  //   const beep = new Audio("sound/qr-code-scan-beep.mp3");
  //   beep.play().catch(err => console.warn("🔇 Audio play failed:", err));
  // };
  const beepRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // beepRef.current = new Audio("/sound/qr-code-scan-beep.mp3");
    const beep = new Audio("/sound/qr-code-scan-beep.mp3");
    beepRef.current = beep;
    beepRef.current.preload = "auto";
  }, []);

  const playBeep = () => {
    if (!beepRef.current) return;
    beepRef.current.currentTime = 0; // rewind in case of rapid re-scans
    beepRef.current.play().catch(err => console.warn("🔇 Audio play failed:", err));
  };

  const resetIdleTimer = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      stopScanning();
      console.log("⏳ QR Scanner stopped due to idle timeout.");
      onIdleTimeout?.();
    }, idleTimeoutMs);
  };

  const stopScanning = () => {
    if (!qrCodeRef.current || !isRunningRef.current) return;

    qrCodeRef.current
      .stop()
      .then(() => {
        isRunningRef.current = false;
        return qrCodeRef.current?.clear();
      })
      .catch((err) => {
        if (err.message?.includes("scanner is not running")) {
          console.log("📷 Scanner already stopped.");
        } else {
          console.error("Error stopping QR scanner:", err);
        }
      });

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  };

  // Build a scan config shared by both the rich and fallback start attempts.
  const buildScanConfig = (): Html5QrcodeCameraScanConfig => ({
    fps,
    qrbox: scanBoxSize
      ? { width: scanBoxSize, height: scanBoxSize }
      : (viewfinderWidth: number, viewfinderHeight: number) => {
        // Adaptive box: 70% of the smaller viewfinder dimension. On most
        // phone screens this ends up noticeably bigger than a fixed
        // 200px box, which means more of the frame (and more pixels per
        // QR module) gets fed into the decoder.
        const minEdge = Math.min(viewfinderWidth, viewfinderHeight);
        const size = Math.floor(minEdge * 0.7);
        return { width: size, height: size };
      },
    aspectRatio: 1.0,
    disableFlip: false,
  });

  const startScanning = async (cameraId: string | { facingMode: string } = { facingMode: "environment" }) => {
    if (!qrCodeRef.current) {
      // formatsToSupport: restrict decoding to QR only — the decoder skips
      // per-frame work trying to match other barcode formats you don't use,
      // which matters on slow/cheap Android CPUs.
      // experimentalFeatures.useBarCodeDetectorIfSupported: on modern Chrome
      // for Android this hands decoding to the OS's native, hardware
      // accelerated BarcodeDetector API instead of the JS/WASM decoder —
      // noticeably faster and more reliable on low-end phones when
      // available, and silently falls back to the JS decoder otherwise.
      try {
        qrCodeRef.current = new Html5Qrcode(elementId, {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true,
          },
          verbose: false,
        } as any);
      } catch (err) {
        console.warn(
          "⚠️ Html5Qrcode rejected extended constructor config (formatsToSupport/experimentalFeatures) — falling back to plain constructor. Consider updating the html5-qrcode package version.",
          err
        );
        qrCodeRef.current = new Html5Qrcode(elementId);
      }
    }

    if (isRunningRef.current) {
      console.log("⚠️ Scanner already running");
      return;
    }

    const successCallback = (decodedText: string) => {
      const now = Date.now();
      const { text: lastText, time: lastTime } = lastScanRef.current;

      // Prevent duplicate scan within 1s or same text as last
      if (decodedText === lastText && now - lastTime < 1000) {
        return;
      }

      lastScanRef.current = { text: decodedText, time: now };

      // console.log("✅ QR Scanned:", decodedText);

      resetIdleTimer();
      if (beepOnSuccess) playBeep();
      onSuccess(decodedText);
    };

    const errorCallback = () => {
      // Optional: Handle scan failures
    };

    const config = buildScanConfig();

    // IMPORTANT: html5-qrcode requires the camera selector object to have
    // EXACTLY ONE key when passed as an object — either { facingMode } or
    // { deviceId }. It rejects a combined MediaTrackConstraints bag (width/
    // height/advanced mixed in), so those go on as a *separate* step below,
    // applied to the already-running track.
    const cameraSelector: string | { facingMode: string } | { deviceId: { exact: string } } =
      typeof cameraId === "string" ? { deviceId: { exact: cameraId } } : cameraId;

    try {
      await qrCodeRef.current.start(cameraSelector, config, successCallback, errorCallback);
      isRunningRef.current = true;
      resetIdleTimer();
    } catch (err) {
      console.error("❌ Failed to start scanner:", err);
      return;
    }

    // Now that the track is live, try to bump resolution and force
    // continuous autofocus. Cheap Android cameras often default to a low-
    // res stream and a slow/fixed focus mode that never locks onto
    // something held close (like a 25mm label) — this fixes both where
    // supported. Wrapped separately so failure here never breaks the scan
    // itself, only loses the sharpness/focus boost.
    try {
      await qrCodeRef.current.applyVideoConstraints({
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        advanced: [{ focusMode: "continuous" } as any],
      });
    } catch (err) {
      console.warn("⚠️ Could not apply high-res/continuous-focus constraints (device/browser may not support it):", err);
    }
  };



  const toggleTorch = async (on: boolean) => {
    if (!qrCodeRef.current || !isRunningRef.current) return false;
    try {
      await qrCodeRef.current.applyVideoConstraints({
        advanced: [{ torch: on } as any],
      });
      return true;
    } catch {
      return false; // not all devices support torch
    }
  };

  const switchCamera = async (deviceId: string) => {
    stopScanning();
    setTimeout(() => {
      startScanning(deviceId);
    }, 300);
  };

  // ADD: apply a zoom level to the running camera track (returns false if unsupported)
  const applyZoom = async (zoomLevel: number): Promise<boolean> => {
    if (!qrCodeRef.current || !isRunningRef.current) return false;

    try {
      const capabilities: any = qrCodeRef.current.getRunningTrackCapabilities();

      if (!capabilities || typeof capabilities.zoom === "undefined") {
        console.warn("⚠️ Zoom not supported by this camera/browser.");
        return false;
      }

      const { min, max } = capabilities.zoom;
      const clampedZoom = Math.min(Math.max(zoomLevel, min), max);

      await qrCodeRef.current.applyVideoConstraints({
        advanced: [{ zoom: clampedZoom } as any],
      });

      return true;
    } catch (err) {
      console.error("❌ Failed to apply zoom:", err);
      return false;
    }
  };


  // ADD: check zoom range without applying anything (used to decide fallback)
  const getZoomCapabilities = (): { min: number; max: number; step: number } | null => {
    if (!qrCodeRef.current || !isRunningRef.current) return null;
    try {
      const capabilities: any = qrCodeRef.current.getRunningTrackCapabilities();
      if (!capabilities || typeof capabilities.zoom === "undefined") return null;
      return capabilities.zoom;
    } catch {
      return null;
    }
  };

  const getCameras = async (): Promise<CameraDevice[]> => {
    return Html5Qrcode.getCameras()
      .then((devices) => devices)
      .catch((err) => {
        console.error("Failed to get cameras:", err);
        return [];
      });
  };

  useEffect(() => {
    return () => {
      stopScanning(); // cleanup on unmount
    };
  }, []);

  return {
    startScanning,
    stopScanning,
    switchCamera,
    getCameras,
    applyZoom,          // ADD
    getZoomCapabilities, // ADD
    toggleTorch,        // ADD THIS

  };
};
